import React, { useState, useEffect } from 'react';
import { TEMPLATES, TEMPLATE_BLANK } from '../lib/boardTemplates';

/**
 * WelcomeSetup — shown the first time a user has zero boards (which only
 * happens at signup, since at least one board always exists thereafter).
 *
 * Three checkboxes, all checked by default. The blank board is locked on so
 * the user is guaranteed at least one workspace they can immediately type
 * into. The two demo boards are opt-out: uncheck if not wanted.
 *
 * On confirm, fires onConfirm(selectedTemplates) — array of full template
 * objects, in the order they should be created. App.js handles the actual
 * creates and the post-create navigation.
 */
export default function WelcomeSetup({ onConfirm }) {
  // Default: all three on. User can uncheck the two demos but not blank.
  const [checked, setChecked] = useState(() => {
    const initial = {};
    TEMPLATES.forEach(t => { initial[t.id] = true; });
    return initial;
  });
  const [submitting, setSubmitting] = useState(false);

  const toggle = (id) => {
    if (id === TEMPLATE_BLANK.id) return; // blank is locked on
    setChecked(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    // Pass the templates in display order so App.js knows which one to
    // navigate to: it'll prefer organizer if checked, else simple, else
    // blank. The data shape is the array of full template objects.
    const selected = TEMPLATES.filter(t => checked[t.id]);
    try {
      await onConfirm(selected);
    } catch (err) {
      console.error('Welcome setup failed:', err);
      setSubmitting(false);
    }
  };

  // Walter 2026-05-26 — Enter anywhere in the modal triggers continue.
  // There's no form here (the modal is plain divs + checkboxes), so
  // the browser's default form-submit behaviour wouldn't fire even if
  // an input had focus. Document-level keydown listener covers all
  // focus states (initial load with nothing focused, after clicking
  // checkboxes, etc.). Guarded against double-fire while submitting.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Enter' || e.shiftKey) return;
      if (submitting) return;
      e.preventDefault();
      handleConfirm();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // Deps: checked + submitting are the values the listener reads
    // from closure (via handleConfirm). Effect re-binds on each
    // change so the next Enter keypress sees the latest state.
  }, [checked, submitting]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 3000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'lists-fadein 0.2s ease',
        padding: '20px',
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border-bright)',
          borderRadius: 'var(--radius)',
          maxWidth: 460,
          width: '100%',
          padding: '28px 28px 22px',
          boxShadow: 'var(--shadow-modal-heavy)',
          fontFamily: 'var(--mono)',
        }}
      >
        <div style={{
          fontSize: 'calc(15px * var(--ui-scale, 1))',
          fontWeight: 600,
          color: 'var(--text)',
          marginBottom: '8px',
        }}>
          welcome to listicity
        </div>

        <div style={{
          fontSize: 'calc(12px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
          lineHeight: 1.55,
          marginBottom: '20px',
        }}>
          We'll set you up with two boards: <strong>my first board</strong>,
          a blank canvas to make your own, and <strong>my organizer board</strong>,
          a ready-made example so you can see what listicity feels like with
          content in it. You'll start on my first board. Uncheck the organizer
          if you'd rather start clean — you can always add or change boards later.
        </div>

        <div style={{ marginBottom: '24px' }}>
          {TEMPLATES.map(t => {
            const isLocked = t.id === TEMPLATE_BLANK.id;
            const isOn = !!checked[t.id];
            return (
              <label
                key={t.id}
                onClick={() => toggle(t.id)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '12px',
                  padding: '10px 12px',
                  marginBottom: '6px',
                  borderRadius: 'var(--radius)',
                  border: `1px solid ${isOn ? 'var(--accent-dim)' : 'var(--border)'}`,
                  background: isOn ? 'var(--accent-wash)' : 'transparent',
                  cursor: isLocked ? 'default' : 'pointer',
                  transition: 'background 0.12s, border-color 0.12s',
                  userSelect: 'none',
                }}
              >
                <Checkbox
                  checked={isOn}
                  locked={isLocked}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 'calc(12px * var(--ui-scale, 1))',
                    fontWeight: 600,
                    color: isOn ? 'var(--text)' : 'var(--text-dim)',
                  }}>
                    {t.welcomeLabel || t.label}
                    {isLocked && (
                      <span style={{
                        marginLeft: '8px',
                        fontSize: 'calc(10px * var(--ui-scale, 1))',
                        fontWeight: 400,
                        color: 'var(--text-dimmer)',
                      }}>
                        always included
                      </span>
                    )}
                  </div>
                  <div style={{
                    marginTop: '3px',
                    fontSize: 'calc(11px * var(--ui-scale, 1))',
                    color: 'var(--text-dim)',
                    lineHeight: 1.5,
                  }}>
                    {t.description}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        {/* Walter 2026-05-10: when 'creating…' first shipped it was
            just a text swap on the button — users (incl. Walter
            testing as a fresh guest) thought the screen had frozen.
            The seed operation can take several seconds when multiple
            templates are checked (Firestore writes for each board +
            its lists). Added a spinning glyph in the button + a
            quiet status line under it so motion confirms the app is
            working. */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px',
        }}>
          <button
            onClick={handleConfirm}
            disabled={submitting}
            style={{
              padding: '9px 20px',
              background: 'var(--accent)',
              color: 'var(--bg)',
              border: '1px solid var(--accent)',
              borderRadius: 'var(--radius)',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(12px * var(--ui-scale, 1))',
              fontWeight: 600,
              cursor: submitting ? 'default' : 'pointer',
              opacity: submitting ? 0.85 : 1,
              transition: 'background 0.12s',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              // Tahmid 2026-05-24: reported needing 3 taps on iPhone
              // before this button registered. `touch-action:
              // manipulation` skips the browser's double-tap-zoom
              // wait and any other tap-gesture latency; cheap
              // defensive fix while we wait for clearer repro data.
              touchAction: 'manipulation',
            }}
            onMouseEnter={e => { if (!submitting) e.currentTarget.style.background = 'var(--accent-dim)'; }}
            onMouseLeave={e => { if (!submitting) e.currentTarget.style.background = 'var(--accent)'; }}
          >
            {submitting && (
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
                   style={{ animation: 'lists-spin 0.9s linear infinite' }}
                   aria-hidden="true">
                <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35"/>
                <path d="M7 2 a5 5 0 0 1 5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            )}
            {submitting ? 'creating…' : 'continue'}
          </button>
          {submitting && (
            <span style={{
              fontFamily: 'var(--mono)',
              fontSize: 'calc(10px * var(--ui-scale, 1))',
              color: 'var(--text-dimmer)',
              letterSpacing: '0.3px',
            }}>
              setting up your boards — this can take a few seconds…
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Custom-rendered checkbox so it matches the rest of the UI's mono/accent
// language (native checkboxes look out of place with the dark theme).
function Checkbox({ checked, locked }) {
  return (
    <span style={{
      width: 16, height: 16,
      flexShrink: 0,
      marginTop: 2,
      borderRadius: '3px',
      border: `1px solid ${checked ? 'var(--accent)' : 'var(--border-bright)'}`,
      background: checked ? 'var(--accent)' : 'transparent',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'background 0.12s, border-color 0.12s',
      opacity: locked ? 0.85 : 1,
    }}>
      {checked && (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M2 5l2 2 4-4" stroke="var(--bg)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </span>
  );
}
